import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { formatFilename } from '../../utils/downloadUtils';
import {
  ChartLayout,
  EquityContent,
  MethodologyContent,
  OverviewContent,
  RelevanceContent
} from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import MeansOfTransportation, { type MeansOfTransportationMetrics } from './MeansOfTransportation';
import ExtremeCommutingPieChart from './ExtremeCommutingPieChart';
import { DepartureTimeChart } from './DepartureTimeChart';
import './ChartLayout.css';

export type CommutePatternView = 'modes' | 'times' | 'departure';

export const COMMUTE_PATTERN_OPTIONS: Array<{
  value: CommutePatternView;
  label: string;
  sectionId: string;
  chartId: string;
}> = [
  {
    value: 'modes',
    label: 'Commute Transportation Modes',
    sectionId: 'means_of_transportation',
    chartId: 'mode_share'
  },
  {
    value: 'times',
    label: 'Commute Times',
    sectionId: 'commute_time_extreme',
    chartId: 'commute_time'
  },
  {
    value: 'departure',
    label: 'Departure time to work',
    sectionId: 'departure_time',
    chartId: 'departure_time'
  }
];

interface CommuteTimesMetrics {
  medianMinutes: number | null;
  year: number | null;
  californiaMedian: number | null;
  extremeCommutePct: number | null;
}

interface Props {
  location: string;
  geofips: number | null;
  chartView: CommutePatternView;
  onChartViewChange: (view: CommutePatternView) => void;
  allowedOptions: Array<{ value: CommutePatternView; label: string }>;
}

const overviewChartIdForView = (view: CommutePatternView): string => {
  if (view === 'times') return 'commute_time';
  if (view === 'departure') return 'departure_time';
  return 'mode_share';
};

export const CommutePatternsSection: React.FC<Props> = ({
  location,
  geofips,
  chartView,
  onChartViewChange,
  allowedOptions
}) => {
  const [activeView, setActiveView] = useState<string>('overview');
  const [modeMetrics, setModeMetrics] = useState<MeansOfTransportationMetrics | null>(null);
  const [topDepartureWindow, setTopDepartureWindow] = useState<string | null>(null);
  const [modesCsvDownload, setModesCsvDownload] = useState<(() => void) | null>(null);
  const [commuteMetrics, setCommuteMetrics] = useState<CommuteTimesMetrics>({
    medianMinutes: null,
    year: null,
    californiaMedian: null,
    extremeCommutePct: null
  });

  const handleModeMetrics = useCallback((metrics: MeansOfTransportationMetrics | null) => {
    setModeMetrics(metrics);
  }, []);

  const handleModesCsvReady = useCallback((fn: (() => void) | null) => {
    setModesCsvDownload(() => fn);
  }, []);

  useEffect(() => {
    if (!location || !geofips) return;
    let cancelled = false;
    const load = async () => {
      try {
        const classifyBucket = (category: string): 'under15' | '15to30' | '30to45' | '45to60' | '60to90' | 'over90' | null => {
          const normalized = (category || '').toLowerCase().replace(/_/g, ' ');
          if (!normalized || normalized === 'total') return null;
          if (normalized.includes('90') || normalized.includes('or more')) return 'over90';
          if (normalized.includes('60')) return '60to90';
          if (normalized.includes('45')) return '45to60';
          if (normalized.includes('35') || normalized.includes('30')) return '30to45';
          if (normalized.includes('25') || normalized.includes('20') || normalized.includes('15')) return '15to30';
          if (normalized.includes('10') || normalized.includes('less') || normalized.includes('under') || normalized.includes('5')) return 'under15';
          return null;
        };

        const bucketBounds: Record<'under15' | '15to30' | '30to45' | '45to60' | '60to90' | 'over90', [number, number]> = {
          under15: [0, 15],
          '15to30': [15, 30],
          '30to45': [30, 45],
          '45to60': [45, 60],
          '60to90': [60, 90],
          over90: [90, 120]
        };

        const bucketOrder: Array<keyof typeof bucketBounds> = ['under15', '15to30', '30to45', '45to60', '60to90', 'over90'];

        const computeSummaryFromRows = (rows: Array<{ year: number; commute_time: string; estimate: number }>) => {
          if (!rows || rows.length === 0) return { median: null as number | null, year: null as number | null, extremePct: null as number | null };
          const latestYear = Math.max(...rows.map((r) => Number(r.year)).filter((y) => !Number.isNaN(y)));
          if (Number.isNaN(latestYear)) return { median: null, year: null, extremePct: null };

          const yearRows = rows.filter((r) => Number(r.year) === latestYear);
          const bucketCounts = new Map<keyof typeof bucketBounds, number>();
          bucketOrder.forEach((b) => bucketCounts.set(b, 0));

          yearRows.forEach((r) => {
            const bucket = classifyBucket(r.commute_time);
            if (!bucket) return;
            const val = Number(r.estimate) || 0;
            bucketCounts.set(bucket, (bucketCounts.get(bucket) || 0) + val);
          });

          const total = bucketOrder.reduce((sum, b) => sum + (bucketCounts.get(b) || 0), 0);
          if (total <= 0) return { median: null, year: latestYear, extremePct: null };
          const extremePct = ((bucketCounts.get('over90') || 0) / total) * 100;

          const midpoint = total / 2;
          let cumulative = 0;
          for (const bucket of bucketOrder) {
            const count = bucketCounts.get(bucket) || 0;
            const prev = cumulative;
            cumulative += count;
            if (count > 0 && midpoint <= cumulative) {
              const [lower, upper] = bucketBounds[bucket];
              const pctInBucket = (midpoint - prev) / count;
              const median = lower + pctInBucket * (upper - lower);
              return { median: Math.round(median * 10) / 10, year: latestYear, extremePct: Math.round(extremePct * 10) / 10 };
            }
          }

          return { median: null, year: latestYear, extremePct: Math.round(extremePct * 10) / 10 };
        };

        const fetchRowsForLocation = async (placeGeofips: string) => {
          const { data: placeData, error: placeError } = await supabaseClient
            .from('extreme_commuting_by_place')
            .select('year, commute_time, estimate')
            .eq('place_geofips', placeGeofips)
            .order('year', { ascending: false });
          if (!placeError && placeData?.length) return placeData as Array<{ year: number; commute_time: string; estimate: number }>;

          const { data: tractData, error: tractError } = await supabaseClient
            .from('extreme_commuting')
            .select('year, commute_time, estimate')
            .eq('geofips', placeGeofips)
            .order('year', { ascending: false });
          if (tractError || !tractData?.length) return [];
          return tractData as Array<{ year: number; commute_time: string; estimate: number }>;
        };

        const [locRows, caRows] = await Promise.all([
          fetchRowsForLocation(String(geofips)),
          fetchRowsForLocation('6')
        ]);
        const locSummary = computeSummaryFromRows(locRows);
        const caSummary = computeSummaryFromRows(caRows);
        if (cancelled) return;
        setCommuteMetrics({
          medianMinutes: locSummary.median,
          year: locSummary.year,
          californiaMedian: caSummary.median,
          extremeCommutePct: locSummary.extremePct
        });
      } catch {
        if (!cancelled) {
          setCommuteMetrics({ medianMinutes: null, year: null, californiaMedian: null, extremeCommutePct: null });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location, geofips]);

  const downloadPng = () => {
    const canvas = document.querySelector('#commute-patterns-chart [data-active] canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const viewLabel =
      chartView === 'times' ? 'Commute_Times' : chartView === 'departure' ? 'Departure_Time' : 'Transportation_Modes';
    const link = document.createElement('a');
    link.download = `${formatFilename(location, `Commute_Patterns_${viewLabel}`)}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const commuteYearLabel = commuteMetrics.year != null ? String(commuteMetrics.year) : '2023';

  const keyMetrics =
    chartView === 'times' ? (
      <KeyMetrics>
        <KeyMetric
          value={commuteMetrics.medianMinutes != null ? `${commuteMetrics.medianMinutes.toFixed(1)} min` : '—'}
          label={`${location} median commute time (${commuteYearLabel})`}
          color="#1e3a8a"
        />
        <KeyMetric
          value={commuteMetrics.californiaMedian != null ? `${commuteMetrics.californiaMedian.toFixed(1)} min` : '—'}
          label={`California median commute time (${commuteYearLabel})`}
          color="#f59e0b"
        />
        <KeyMetric
          value={commuteMetrics.extremeCommutePct != null ? `${commuteMetrics.extremeCommutePct.toFixed(1)}%` : '—'}
          label="Extreme Commutes (%)"
          color="#ef4444"
        />
      </KeyMetrics>
    ) : chartView === 'departure' ? (
      <KeyMetrics>
        <KeyMetric
          value={topDepartureWindow || '–'}
          label="Most common departure window"
          color="#2563eb"
        />
      </KeyMetrics>
    ) : (
      <KeyMetrics>
        <KeyMetric
          value={
            modeMetrics?.carPointChange != null
              ? `${modeMetrics.carPointChange > 0 ? '+' : ''}${modeMetrics.carPointChange.toFixed(1)} pts`
              : 'Loading...'
          }
          label="Car/Truck/Van Change Since 2019 (pts)"
          color={modeMetrics?.carPointChange != null && modeMetrics.carPointChange < 0 ? '#10b981' : '#ef4444'}
        />
        <KeyMetric
          value={
            modeMetrics?.wfhPointChange != null
              ? `${modeMetrics.wfhPointChange > 0 ? '+' : ''}${modeMetrics.wfhPointChange.toFixed(1)} pts`
              : 'Loading...'
          }
          label="Work From Home Change Since 2019 (pts)"
          color={modeMetrics?.wfhPointChange != null && modeMetrics.wfhPointChange > 0 ? '#10b981' : '#ef4444'}
        />
        <KeyMetric
          value={modeMetrics ? String(modeMetrics.latestYear) : 'Loading...'}
          label="Latest Year"
          color="#1e3a8a"
        />
      </KeyMetrics>
    );

  const overviewContent =
    chartView === 'times' ? (
      <OverviewContent
        title="Commute Times"
        description="Average commute time and the share of workers with long (90+ minute one-way) commutes help identify jobs-housing balance and transportation burden."
      >
        <p>
          {commuteMetrics.medianMinutes != null ? (
            <>
              In {location}, the median one-way commute to work was {commuteMetrics.medianMinutes.toFixed(1)} minutes
              {commuteMetrics.year ? ` (${commuteMetrics.year})` : ''}.
              {commuteMetrics.californiaMedian != null && (
                <> That is {commuteMetrics.medianMinutes > commuteMetrics.californiaMedian ? 'above' : 'below'} the California median of {commuteMetrics.californiaMedian.toFixed(1)} minutes.</>
              )}
              {' '}The pie chart shows how workers are distributed across commute time brackets; the “90+ min (extreme)” slice is the share of workers with commutes of 90 minutes or more one-way.
            </>
          ) : (
            `Commute time data for ${location} comes from the American Community Survey (ACS) and is allocated from census tracts to places using block group–place relationship files.`
          )}
        </p>
      </OverviewContent>
    ) : chartView === 'departure' ? (
      <OverviewContent
        title="Departure Time Overview"
        description="Departure time to work shows when residents leave for work, which affects congestion patterns, transit demand, and transportation emissions."
      >
        <p>
          Earlier departure times can indicate long commutes or shift work, while later departures may reflect flexible schedules or closer jobs.
          Understanding this pattern helps design transit service, manage peak congestion, and align climate policies with real travel behavior.
        </p>
      </OverviewContent>
    ) : (
      <OverviewContent
        title="Transportation Mode Analysis"
        description="This chart shows how workers commute to their jobs across different transportation modes over time. Understanding these patterns is essential for climate planning as it informs decisions on infrastructure investments for sustainable transportation methods."
      >
        <div>
          {modeMetrics ? (
            <p>
              {modeMetrics.car2019 != null && modeMetrics.carLatest != null && modeMetrics.carPointChange != null ? (
                <>
                  Car, truck, or van commuting {modeMetrics.carPointChange < 0 ? 'decreased' : 'increased'} by {Math.abs(modeMetrics.carPointChange).toFixed(1)} percentage points
                  (from {modeMetrics.car2019.toFixed(1)}% to {modeMetrics.carLatest.toFixed(1)}%){modeMetrics.carRelativeChange != null ? `, a ${Math.abs(modeMetrics.carRelativeChange).toFixed(0)}% relative change` : ''} since {modeMetrics.baselineYear}.
                </>
              ) : null}{' '}
              {modeMetrics.transit2019 != null && modeMetrics.transitLatest != null && modeMetrics.transitPointChange != null ? (
                <>
                  Public transit {modeMetrics.transitPointChange < 0 ? 'decreased' : 'increased'} by {Math.abs(modeMetrics.transitPointChange).toFixed(1)} percentage points
                  (from {modeMetrics.transit2019.toFixed(1)}% to {modeMetrics.transitLatest.toFixed(1)}%){modeMetrics.transitRelativeChange != null ? `, a ${Math.abs(modeMetrics.transitRelativeChange).toFixed(0)}% relative change` : ''} since {modeMetrics.baselineYear}.
                </>
              ) : null}{' '}
              {modeMetrics.wfh2019 != null && modeMetrics.wfhLatest != null && modeMetrics.wfhPointChange != null ? (
                <>
                  Worked from home {modeMetrics.wfhPointChange < 0 ? 'decreased' : 'increased'} by {Math.abs(modeMetrics.wfhPointChange).toFixed(1)} percentage points
                  (from {modeMetrics.wfh2019.toFixed(1)}% to {modeMetrics.wfhLatest.toFixed(1)}%){modeMetrics.wfhRelativeChange != null ? `, a ${Math.abs(modeMetrics.wfhRelativeChange).toFixed(0)}% relative change` : ''} since {modeMetrics.baselineYear}.
                </>
              ) : null}
            </p>
          ) : (
            <div>
              <p><strong>Key Transportation Modes:</strong></p>
              <ul>
                <li><strong>Car, Truck, or Van</strong> (typically 70-85% of commutes)</li>
                <li><strong>Public Transit</strong> - buses, trains, subways</li>
                <li><strong>Bicycle</strong> - active transportation</li>
                <li><strong>Walked</strong> - pedestrian commuting</li>
                <li><strong>Worked from Home</strong> - remote work</li>
                <li><strong>Other Means</strong> - motorcycle, taxicab, etc.</li>
              </ul>
            </div>
          )}
        </div>
      </OverviewContent>
    );

  const relevanceContent =
    chartView === 'times' ? (
      <RelevanceContent
        title="Policy relevance"
        description="Commute times and extreme commuting matter for climate, equity, and quality of life."
      >
        <div className="methodology-details-simple">
          <h5>VMT and emissions</h5>
          <p>Longer commutes mean more vehicle miles traveled and higher transportation emissions. Reducing extreme commuting supports climate goals.</p>
        </div>
        <div className="methodology-details-simple">
          <h5>Jobs–housing balance</h5>
          <p>High shares of extreme commuters often indicate a mismatch between where people live and where jobs are, and can point to housing and transit investments.</p>
        </div>
      </RelevanceContent>
    ) : chartView === 'departure' ? (
      <RelevanceContent
        title="Relevance to Climate Policy"
        description="Departure time patterns shape peak-period congestion and emissions."
      >
        <p>
          When many workers travel during the same peak hours, congestion and per‑mile emissions rise. Policies that support flexible work hours,
          telecommuting, and high‑frequency transit during peak periods can reduce congestion and emissions while maintaining access to jobs.
        </p>
      </RelevanceContent>
    ) : (
      <>
        <h4>Policy Relevance</h4>
        <p className="section-subtitle">Transportation mode choice affects emissions and travel demand.</p>
        <div className="narrative-details-box">
          <p><strong>Travel Patterns:</strong></p>
          <p>Commute mode share helps track where trips are shifting and where options are limited.</p>
          <ul>
            <li>Identify high‑emission modes and track shifts to lower‑carbon options</li>
            <li>Target transit upgrades where they are most needed</li>
            <li>Prioritize walking and biking connections</li>
            <li>Plan for electric vehicle adoption</li>
            <li>Support land use and mobility planning that reduces vehicle miles traveled</li>
          </ul>
          <p>This connects to air quality, congestion, public health, and access to jobs and services.</p>
        </div>
      </>
    );

  const equityContent =
    chartView === 'times' ? (
      <EquityContent
        title="Equity considerations"
        description="Long and extreme commutes often fall disproportionately on lower-income and minority workers."
      >
        <div className="methodology-details-simple">
          <p>Lower-income households are more likely to live farther from job centers due to housing costs, leading to longer commutes. Tracking extreme commuting by place helps target transit and housing policies.</p>
        </div>
      </EquityContent>
    ) : chartView === 'departure' ? (
      <EquityContent
        title="Equity Considerations"
        description="Not all workers can shift their schedules or choose nearby jobs."
      >
        <p>
          Low‑wage workers and shift workers are more likely to have rigid schedules and early or late departure times, often with fewer transit options.
          Climate and transportation policies should consider whether early‑morning and late‑night service is available for these workers and whether
          housing near jobs is affordable.
        </p>
      </EquityContent>
    ) : (
      <>
        <h4>Equity Considerations</h4>
        <p className="section-subtitle section-subtitle-black">
          Access to low-carbon transportation options is uneven across communities.
        </p>
        <div className="narrative-details-box">
          <p><strong>Transportation Equity Challenges:</strong></p>
          <ul>
            <li>Some communities have limited access to transit, biking, and walking infrastructure</li>
            <li>Longer commutes and poor service can increase time and cost burdens</li>
            <li>Work-from-home options are not equally available across job types</li>
            <li>Shifting away from driving requires affordable and accessible alternatives</li>
          </ul>
          <p>Expanding transportation options can reduce these gaps and improve access for more communities.</p>
        </div>
      </>
    );

  const methodologyContent =
    chartView === 'times' ? (
      <MethodologyContent
        title="Data and methodology"
        description="Tract-level ACS data is allocated to places using block group–place relationship files."
        dataSources={[
          'acs5_commute_time (tract-level mean travel time to work, ACS Table B08303)',
          'extreme_commuting (tract-level workers by commute time, ACS Table B08012)',
          'relationship_bgrp_place_10 and relationship_bgrp_place_20 (tract derived as bg_geofips/10)'
        ]}
        methodology={[
          'Average commute time: from pre-aggregated commute_time table (tract → place via relationship_bgrp_place, see populate_commute_time_from_acs5.sql).',
          'Extreme commuting pie: tract-level extreme_commuting is allocated to places using relationship_bgrp_place_20 (years ≥ 2019) and relationship_bgrp_place_10 (years &lt; 2019), with tract = bg_geofips/10. See docs/TRACT_OR_BLOCKGROUP_TO_PLACE_COUNTY_GUIDE.md.'
        ]}
      />
    ) : chartView === 'departure' ? (
      <MethodologyContent
        title="Data Sources and Methodology"
        description="Departure time metrics use ACS 5‑Year tract data aggregated to place and county."
        dataSources={[
          'ACS 5‑Year (Journey to Work – Departure Time)',
          'departure_time_to_work table (aggregated from tract‑level ACS data)'
        ]}
        methodology={[
          'Tract‑level ACS 5‑Year estimates of workers by departure time window are aggregated to place and county geographies using Census relationship files.',
          'For each geography and year, the number of workers departing in each time window is summed and, when not already present, converted to a percent of total workers.',
          'Charts show the distribution of workers by departure window for the most recent year with complete data.'
        ]}
      />
    ) : (
      <MethodologyContent
        title="Data Sources and Methodology"
        description="This analysis uses American Community Survey (ACS) 5-Year sample data, with the final year of the sample period representing the current reporting year."
        dataSources={[
          'American Community Survey (ACS) 5-Year Estimates',
          'ACS Table B08301: Means of Transportation to Work'
        ]}
        methodology={[
          'The percentage of workers by mode of transportation is calculated by dividing the number of workers using each transportation type by the total number of workers',
          'These values represent modal shares across all commuting methods reported in the ACS data',
          'Data filtered by county geofips for geographic specificity',
          'Time series analysis from 2010-2023 where data is available',
          'Each mode shown as a percentage of total workers to enable comparison across modes and over time'
        ]}
      />
    );

  const pngLabel =
    chartView === 'times' ? 'Commute_Times' : chartView === 'departure' ? 'Departure_Time' : 'Transportation_Modes';

  return (
    <>
      <div id="means_of_transportation" />
      <div id="commute_time_extreme" />
      <div id="departure_time" />
    <ChartLayout
      className={chartView === 'times' ? 'commute-patterns commute-extreme-compact' : 'commute-patterns'}
      title="Commute Patterns"
      description={`${location} • Source: American Community Survey (ACS) 5-Year Estimates`}
      chartType={chartView === 'modes' ? 'line' : 'bar'}
      chartSelector={{
        options: allowedOptions,
        value: chartView,
        onChange: (value: string) => onChartViewChange(value as CommutePatternView)
      }}
      keyMetrics={keyMetrics}
      chartContent={
        <div id="commute-patterns-chart" style={{ position: 'relative', minHeight: 420 }}>
          <div
            data-active={chartView === 'modes' ? 'true' : undefined}
            style={chartView === 'modes' ? undefined : { position: 'absolute', left: -9999, width: '100%' }}
          >
            <MeansOfTransportation
              location={location}
              geofips={geofips}
              embedOnly
              onMetricsCalculated={handleModeMetrics}
              onDownloadCSVReady={handleModesCsvReady}
            />
          </div>
          <div
            data-active={chartView === 'times' ? 'true' : undefined}
            style={chartView === 'times' ? undefined : { position: 'absolute', left: -9999, width: '100%' }}
          >
            <ExtremeCommutingPieChart location={location} hideInternalHeading />
          </div>
          <div
            data-active={chartView === 'departure' ? 'true' : undefined}
            style={chartView === 'departure' ? undefined : { position: 'absolute', left: -9999, width: '100%' }}
          >
            <DepartureTimeChart
              location={location}
              hideInternalHeading
              onTopWindowChange={setTopDepartureWindow}
            />
          </div>
        </div>
      }
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      equityContent={equityContent}
      methodologyContent={methodologyContent}
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
      overviewSectionId="transportation"
      overviewChartId={overviewChartIdForView(chartView)}
      scrollAnchorId="commute_patterns"
      onDownloadPNG={downloadPng}
      onDownloadCSV={chartView === 'modes' ? modesCsvDownload ?? undefined : undefined}
      pngFilename={formatFilename(location, `Commute_Patterns_${pngLabel}`)}
      csvFilename={formatFilename(location, 'Means_of_Transportation_Data')}
      registerPng={false}
    />
    </>
  );
};

export default CommutePatternsSection;
